/**
 * Entrar com a conta da Microsoft, no portal.
 *
 * Por que isto é escrito à mão em vez de usar a MSAL: precisamos de **uma** coisa, **uma** vez — um
 * id_token para trocar pela sessão do NINA. Não há chamada ao Graph pelo navegador, não há cache de
 * token a manter, não há renovação silenciosa (quem renova é o refresh do NINA). A MSAL resolve
 * todos esses problemas que não temos, e traria ~200 KB e mais uma dependência para um build de CRA
 * que já quebrou em produção por warning de ESLint.
 *
 * ⚠ E o que torna a decisão segura: **nada aqui valida o token**. Quem valida assinatura, emissor,
 * audiência e validade é o servidor (`Auth/EntraToken.cs`). Um erro neste arquivo faz o login não
 * funcionar; não faz o login aceitar quem não devia.
 *
 * Se um dia o portal precisar falar com o Graph direto, a conta muda e a MSAL passa a valer a pena.
 */

const TENANT_ID = "227d8aee-9b50-4cd3-8628-7d9e83724bde";
const CLIENT_ID = "364673be-6409-4499-b422-a64715344299";
const AUTHORITY = `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0`;

/** `openid profile email` bastam: só queremos saber quem é a pessoa. */
const SCOPES = "openid profile email";

const CHAVE_VERIFIER = "entra_pkce_verifier";
const CHAVE_ESTADO = "entra_state";

/** Base64 URL-safe, o formato que o PKCE exige (sem `+`, `/` nem `=`). */
export function base64Url(bytes: Uint8Array): string {
  let binario = "";
  bytes.forEach((b) => {
    binario += String.fromCharCode(b);
  });
  return btoa(binario).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Texto aleatório para o `code_verifier` e para o `state`. */
export function aleatorio(bytes = 32): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return base64Url(buffer);
}

export async function desafioDe(verifier: string): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(hash));
}

/**
 * Endereço de retorno: a **tela de login**, não a raiz do site.
 *
 * ⚠ Isto já apontou para a origem (`https://portal.datavalue.com.br`) e não funcionava — o sintoma
 * era clicar em "Entrar com a Microsoft" e voltar para a tela de login, sem erro nenhum. A causa:
 * a rota `/` faz `<Navigate to="/auth/sign-in" replace />`, e **navegar descarta a query string**.
 * O `?code=` chegava e era jogado fora antes de qualquer código nosso ver.
 *
 * O retorno tem que cair na página que sabe tratá-lo. Exige que o servidor sirva o deep link
 * (`navigationFallback` do Static Web Apps, que já existe).
 */
export const CAMINHO_DE_RETORNO = "/auth/sign-in";

export function redirectUri(): string {
  return window.location.origin + CAMINHO_DE_RETORNO;
}

/**
 * Monta a URL de autorização. Separada do `window.location` para caber em teste — é onde um
 * parâmetro trocado (o `code_challenge_method` em `plain`, por exemplo) passaria despercebido.
 */
export function urlDeAutorizacao(desafio: string, estado: string, loginHint?: string): string {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    redirect_uri: redirectUri(),
    response_mode: "query",
    scope: SCOPES,
    state: estado,
    code_challenge: desafio,
    code_challenge_method: "S256",
  });
  // Convite: a Microsoft já abre com o e-mail do convite preenchido.
  if (loginHint) params.set("login_hint", loginHint);
  return `${AUTHORITY}/authorize?${params.toString()}`;
}

/** Leva a pessoa para a Microsoft. Guarda o verifier e o state para conferir na volta. */
export async function iniciarLogin(loginHint?: string): Promise<void> {
  const verifier = aleatorio();
  const estado = aleatorio(16);

  sessionStorage.setItem(CHAVE_VERIFIER, verifier);
  sessionStorage.setItem(CHAVE_ESTADO, estado);

  window.location.assign(urlDeAutorizacao(await desafioDe(verifier), estado, loginHint));
}

/** O que a volta da Microsoft trouxe na URL. */
export type RetornoEntra =
  | { tipo: "nenhum" }
  | { tipo: "erro"; mensagem: string }
  | { tipo: "codigo"; codigo: string; verifier: string };

/**
 * Lê o retorno da Microsoft.
 *
 * ⚠ O `state` é conferido aqui, e não é formalidade: sem ele, um link montado por terceiro
 * ("clique aqui") faria o portal trocar um código que **não foi esta pessoa** que pediu, e a sessão
 * resultante seria de outra conta. É o único ponto deste arquivo onde a conferência importa de fato.
 */
export function lerRetorno(busca: string): RetornoEntra {
  const params = new URLSearchParams(busca);

  const erro = params.get("error");
  if (erro) {
    return { tipo: "erro", mensagem: params.get("error_description") || erro };
  }

  const codigo = params.get("code");
  if (!codigo) return { tipo: "nenhum" };

  const esperado = sessionStorage.getItem(CHAVE_ESTADO);
  if (!esperado || params.get("state") !== esperado) {
    return { tipo: "erro", mensagem: "Retorno de login não confere. Tente entrar de novo." };
  }

  const verifier = sessionStorage.getItem(CHAVE_VERIFIER);
  if (!verifier) {
    return { tipo: "erro", mensagem: "Sessão de login expirou. Tente entrar de novo." };
  }

  return { tipo: "codigo", codigo, verifier };
}

export function limparRetorno(): void {
  sessionStorage.removeItem(CHAVE_VERIFIER);
  sessionStorage.removeItem(CHAVE_ESTADO);
  // Tira `?code=…` da barra de endereço: recarregar a página tentaria trocar um código já usado.
  window.history.replaceState({}, document.title, window.location.pathname);
}

/** Troca o código pelo id_token direto com a Microsoft (cliente público, sem segredo). */
export async function trocarPorIdToken(codigo: string, verifier: string): Promise<string> {
  const corpo = new URLSearchParams({
    client_id: CLIENT_ID,
    grant_type: "authorization_code",
    code: codigo,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
    scope: SCOPES,
  });

  const resposta = await fetch(`${AUTHORITY}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: corpo.toString(),
  });

  const dados = await resposta.json();
  if (!resposta.ok || !dados.id_token) {
    throw new Error(dados.error_description || "Não foi possível confirmar sua conta na Microsoft.");
  }

  return dados.id_token as string;
}

const CHAVE_LINK_CONVITE = "linkConviteMicrosoft";

/** Guarda o token do convite para a volta da Microsoft ligá-lo à conta (`linkToken`). */
export function guardarLinkConvite(token: string): void {
  sessionStorage.setItem(CHAVE_LINK_CONVITE, token);
}

/** Lê e apaga o token do convite: uso único, dê certo ou erre o login. */
export function consumirLinkConvite(): string | null {
  const token = sessionStorage.getItem(CHAVE_LINK_CONVITE);
  sessionStorage.removeItem(CHAVE_LINK_CONVITE);
  return token;
}
